import { Bug, Keyboard, LogOut, MessageSquareText } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';
import { MenuBlock } from './MenuBlock';
import { PopoverMenu } from './PopoverMenu';
import type {
  AppSelectorInfo,
  HeaderMenuItem,
  ProfileMenuItem,
  SidebarMenuItem,
  User,
} from './sidebar-types';
import { useSidebarOptions } from './useSidebarOptions';
import { useSidebarShowSubItems } from './useSidebarShowSubItems';
import srIcon from '@/assets/loans-icon.svg';
import { LOANS_APP_ROLE_QUERY_KEY, isLoansAppRole, resolveCurrentAppRole } from '@/lib/app-role';
import { cn } from '@/lib/cn';

export interface SidebarProps {
  avatarUrl?: string;
  label?: ReactNode;
  minimizable?: boolean;
  searchField?: boolean;
  defaultOpened?: boolean;
  user?: User | null;
  appSelectorInfo?: AppSelectorInfo;
  headerMenuItems?: HeaderMenuItem[];
  headerMenuSelectedItem?: HeaderMenuItem;
  isHeaderMenuItemsLoading?: boolean;
  disabledHeaderMenu?: boolean;
  onLogout: () => void;
  onOpenShortcuts: () => void;
  onOpenBugReport: () => void;
  onOpenFeatureRequest: () => void;
  profileMenuItems?: ProfileMenuItem[];
  profileHeaderMenu?: ReactNode;
  mobile?: boolean;
  onCloseMobile?: () => void;
  bootId?: string;
}

function getUserInitials(firstName?: string, lastName?: string) {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

function resolveDisplayName(user?: User | null) {
  return (
    [user?.firstName || user?.first_name, user?.lastName || user?.last_name]
      .filter(Boolean)
      .join(' ') ||
    user?.email ||
    'Loans User'
  );
}

function resolveJobTitle(user?: User | null) {
  return user?.jobTitle || user?.role || user?.business?.name || '';
}

function formatRoleLabel(value: string) {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function itemContainsPath(item: SidebarMenuItem, pathname: string): boolean {
  if (item.pathname && item.pathname === pathname) {
    return true;
  }

  if (item.subItems?.some(subItem => itemContainsPath(subItem, pathname))) {
    return true;
  }

  if (
    item.subSections?.some(section =>
      section.items.some(subItem => itemContainsPath(subItem, pathname)),
    )
  ) {
    return true;
  }

  return false;
}

function buildInitialOpenItems(menuItems: SidebarMenuItem[], pathname: string) {
  const openItems: Record<string, boolean> = {};

  menuItems.forEach((item, index) => {
    const itemId = `item_${index}`;
    const containsActivePath = itemContainsPath(item, pathname);
    openItems[itemId] = containsActivePath || item.defaultOpen === true;

    item.subItems?.forEach((_, subIndex) => {
      openItems[`${itemId}_${subIndex}`] = false;
    });

    item.subSections?.forEach((section, sectionIndex) => {
      const sectionId = `${itemId}_section_${sectionIndex}`;
      openItems[sectionId] = section.items.some(subItem => itemContainsPath(subItem, pathname));

      section.items.forEach((_, itemIndex) => {
        openItems[`${sectionId}_${itemIndex}`] = false;
      });
    });
  });

  return openItems;
}

export function SidebarPanel({
  label = 'COMPLI ONE',
  minimizable = true,
  searchField = true,
  defaultOpened,
  user,
  avatarUrl,
  appSelectorInfo,
  headerMenuItems,
  headerMenuSelectedItem,
  isHeaderMenuItemsLoading,
  disabledHeaderMenu,
  onLogout,
  onOpenShortcuts,
  onOpenBugReport,
  onOpenFeatureRequest,
  profileMenuItems,
  profileHeaderMenu,
  mobile = false,
  onCloseMobile: _onCloseMobile,
  bootId = 'default',
}: SidebarProps) {
  const [isOpened, setIsOpened] = useState(defaultOpened ?? (!mobile && window.innerWidth > 1024));
  const [search, setSearch] = useState('');
  const location = useLocation();
  const menuItems = useSidebarOptions();
  const {
    openAll,
    closeAll,
    resetOpenItems,
    sessionId,
    bootId: storedBootId,
    setSessionId,
    setBootId,
  } = useSidebarShowSubItems();
  const requestedAppRole = new URLSearchParams(location.search).get(LOANS_APP_ROLE_QUERY_KEY);
  const appRole = isLoansAppRole(requestedAppRole) ? requestedAppRole : resolveCurrentAppRole(user);
  const currentSessionId = `${user?.id ?? 'anonymous'}:${appRole}`;
  const resolvedProfileMenuItems = profileMenuItems ?? [
    { label: 'Shortcuts', onClick: onOpenShortcuts, icon: Keyboard },
    { label: 'Feature request', onClick: onOpenFeatureRequest, icon: MessageSquareText },
    { label: 'Report a bug', onClick: onOpenBugReport, icon: Bug },
    { label: 'Sign out', onClick: onLogout, icon: LogOut, danger: true },
  ];

  useEffect(() => {
    if (defaultOpened === undefined) return;
    setIsOpened(defaultOpened);
  }, [defaultOpened]);

  useEffect(() => {
    if (storedBootId !== bootId || sessionId !== currentSessionId) {
      setBootId(bootId);
      setSessionId(currentSessionId);
      const nextOpenItems = buildInitialOpenItems(menuItems, location.pathname);
      const frameId = window.requestAnimationFrame(() => {
        useSidebarShowSubItems.setState({
          sessionId: currentSessionId,
          bootId,
        });
        resetOpenItems(nextOpenItems);
      });

      return () => window.cancelAnimationFrame(frameId);
    }
  }, [
    bootId,
    currentSessionId,
    location.pathname,
    menuItems,
    sessionId,
    setBootId,
    setSessionId,
    storedBootId,
  ]);

  useEffect(() => {
    const expectedOpenItems = buildInitialOpenItems(menuItems, location.pathname);

    useSidebarShowSubItems.setState(state => ({
      openItems: {
        ...state.openItems,
        ...Object.fromEntries(Object.entries(expectedOpenItems).filter(([, isOpen]) => isOpen)),
      },
    }));
  }, [location.pathname, menuItems]);

  const resolveItems = useMemo(() => {
    let count = 0;
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return { items: menuItems, count };

    const items = menuItems
      .filter(item => {
        const labelMatches =
          typeof item.label === 'string' && item.label.toLowerCase().includes(normalizedSearch);
        const keywordsMatches = item.searchKeywords?.some(keyword =>
          keyword.toLowerCase().includes(normalizedSearch),
        );
        const subItems = item.subItems || [];
        const subSections = item.subSections || [];
        const subItemLabelMatches = subItems.some(
          ({ label }) =>
            typeof label === 'string' && label.toLowerCase().includes(normalizedSearch),
        );
        const subItemKeywordMatches = subItems.some(({ searchKeywords = [] }) =>
          searchKeywords.some(keyword => keyword.toLowerCase().includes(normalizedSearch)),
        );
        const subSectionLabelMatches = subSections.some(({ label }) =>
          label.toLowerCase().includes(normalizedSearch),
        );
        const subSectionItemLabelMatches = subSections.some(({ items }) =>
          items.some(
            ({ label }) =>
              typeof label === 'string' && label.toLowerCase().includes(normalizedSearch),
          ),
        );
        const subSectionItemKeywordMatches = subSections.some(({ items }) =>
          items.some(({ searchKeywords = [] }) =>
            searchKeywords.some(keyword => keyword.toLowerCase().includes(normalizedSearch)),
          ),
        );

        if (labelMatches || keywordsMatches) count += 1;
        return (
          labelMatches ||
          keywordsMatches ||
          subItemLabelMatches ||
          subItemKeywordMatches ||
          subSectionLabelMatches ||
          subSectionItemLabelMatches ||
          subSectionItemKeywordMatches
        );
      })
      .map(item => ({
        ...item,
        subItems: item.subItems?.filter(({ label, searchKeywords = [] }) => {
          const labelMatches =
            typeof label === 'string' && label.toLowerCase().includes(normalizedSearch);
          const keywordsMatches = searchKeywords.some(keyword =>
            keyword.toLowerCase().includes(normalizedSearch),
          );

          if (labelMatches || keywordsMatches) count += 1;
          return labelMatches || keywordsMatches;
        }),
        subSections: item.subSections
          ?.map(section => ({
            ...section,
            items: section.items.filter(({ label, searchKeywords = [] }) => {
              const labelMatches =
                typeof label === 'string' && label.toLowerCase().includes(normalizedSearch);
              const keywordsMatches = searchKeywords.some(keyword =>
                keyword.toLowerCase().includes(normalizedSearch),
              );

              if (labelMatches || keywordsMatches) count += 1;
              return labelMatches || keywordsMatches;
            }),
          }))
          .filter(
            section =>
              section.label.toLowerCase().includes(normalizedSearch) || section.items.length > 0,
          ),
      }));

    return { items, count };
  }, [menuItems, search]);

  const handleChangeSearch = (value: string) => {
    setSearch(oldValue => {
      if (!oldValue && value) openAll();
      if (oldValue && !value) closeAll();
      return value;
    });
  };

  return (
    <aside
      className={cn('bg-[#171717] text-white transition-all', {
        'hidden h-dvh lg:flex lg:flex-col lg:items-start lg:justify-start': !mobile,
        'fixed inset-0 top-14 z-40 flex h-[calc(100dvh-56px)] w-full flex-col lg:hidden': mobile,
        'w-[240px] min-w-[240px]': isOpened && !mobile,
        'w-[72px] min-w-[72px]': !isOpened && !mobile,
      })}
    >
      <div className="flex h-full w-full flex-col">
        <Header
          isOpened={isOpened}
          label={
            <div className="flex w-full items-center gap-2">
              <Link to="/dashboard" className="flex min-w-0 items-center gap-2">
                <img src={srIcon} alt="CompliOne Loans" className="h-8 w-8 shrink-0" />
                {isOpened && (
                  <div className="min-w-0">
                    <div className="truncate font-serif text-[15px] font-medium uppercase leading-[14px] text-white">
                      {label}
                    </div>
                    <div className="truncate pt-[2px] text-[8px] uppercase leading-[10px] tracking-[0.12em] text-white/80">
                      Loan Management
                    </div>
                  </div>
                )}
              </Link>
              {mobile && _onCloseMobile && (
                <button
                  type="button"
                  onClick={_onCloseMobile}
                  className="ml-auto rounded-md p-2 text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                  aria-label="Close menu"
                >
                  Close
                </button>
              )}
            </div>
          }
          minimizable={!mobile && minimizable}
          handleChangeOpen={() => setIsOpened(open => !open)}
          appSelectorInfo={appSelectorInfo}
          headerMenuItems={headerMenuItems}
          headerMenuSelectedItem={headerMenuSelectedItem}
          isHeaderMenuItemsLoading={isHeaderMenuItemsLoading}
          disabledHeaderMenu={disabledHeaderMenu}
          search={search}
          resolveItemsCount={resolveItems.count}
          searchField={searchField}
          setSearch={handleChangeSearch}
        />

        {mobile && _onCloseMobile && (
          <div className="px-4 pb-3 lg:hidden">
            <button
              type="button"
              onClick={_onCloseMobile}
              className="rounded-md px-2 py-1 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white"
              aria-label="Close menu"
            >
              Close
            </button>
          </div>
        )}

        <MenuBlock
          menuItems={resolveItems.items}
          isOpened={isOpened}
          setIsOpened={setIsOpened}
          isSearchUsed={!!search}
          search={search}
        />

        {user && (
          <PopoverMenu
            items={resolvedProfileMenuItems}
            headerMenu={profileHeaderMenu ? <>{profileHeaderMenu}</> : undefined}
            placement="top-start"
            classes={{ panel: 'p-0 rounded min-w-[200px] bg-white z-[2] border border-gray-200' }}
            button={
              <div className="mt-auto w-full flex items-center px-6 pb-6 space-x-3 text-left">
                <div className="overflow-hidden flex justify-center items-center text-neutral-400 bg-neutral-200 hover:scale-110 transition-all relative w-9 h-9 min-w-[36px] min-h-[36px] rounded-full">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="object-cover h-full w-full" />
                  ) : (
                    <span
                      className="flex items-center justify-center select-none font-bold text-neutral-400 bg-neutral-200 leading-[22px] text-nowrap text-[16px]"
                      title={resolveDisplayName(user)}
                    >
                      {getUserInitials(
                        user.firstName || user.first_name,
                        user.lastName || user.last_name,
                      )}
                    </span>
                  )}
                </div>
                {isOpened && (
                  <div className="flex flex-col items-start capitalize min-w-0">
                    <div className="flex flex-col items-start space-y-1 min-w-0">
                      <div className="truncate text-[14px] leading-[22px] text-white">
                        {resolveDisplayName(user)}
                      </div>
                      <div className="w-full flex items-center space-x-1 min-w-0">
                        <div className="truncate text-[11px] leading-[13px] text-start text-white/60">
                          {formatRoleLabel(resolveJobTitle(user))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        )}
      </div>
    </aside>
  );
}

export function Sidebar(props: SidebarProps) {
  return <SidebarPanel {...props} />;
}
